import {useCallback, useState} from 'react'
import {useCreateConversationMutation, useCreateMessageMutation} from '@apis/aiCoreAPI'
import {openAlpinistPanel} from '@shared/hooks/useAlpinistUrlSync'
import {useSnackbar} from '@shared/hooks/useSnackbar'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import type {PublishedPageContext} from '@shared/alpinist/pageContext'
import {usePageContextRegistry} from '@shared/alpinist/pageContext'

type AskAlpinistArgs = {
  /** Title of the conversation created for this shortcut. */
  conversationTitle: string
  /** Fully-built question seeded as the first user message. */
  prompt: string
  /**
   * Called right after the Alpinist panel has opened on the new conversation,
   * before the seeded message resolves. A host that covers the panel (a dialog,
   * a modal) closes itself here; a failed conversation never reaches it, so the
   * host stays where the error snackbar is shown (AB#6464).
   */
  onOpened?: () => void
  /**
   * What the widget the shortcut is asked from shows (the batch, its equipment
   * and window, ...), sent as the message's page_context over the page's own
   * (AB#6762). Omitted = the page's context alone.
   */
  pageContext?: PublishedPageContext
}

/**
 * useAlpinistShortcut - Ask Alpinist a canned question: creates a fresh
 * conversation seeded with the prompt and opens the Alpinist panel on it. The
 * panel is opened right after the conversation exists (before the message
 * POST resolves) so the user lands on the conversation while the agent
 * responds. Failures surface as an error snackbar.
 */
export const useAlpinistShortcut = (): {
  askAlpinist: (args: AskAlpinistArgs) => Promise<void>
  asking: boolean
} => {
  const [t] = useCustomTranslation('alpinist')
  const [createConversation] = useCreateConversationMutation()
  const [createMessage] = useCreateMessageMutation()
  const [showNotification] = useSnackbar()
  const [asking, setAsking] = useState(false)
  const pageContextRegistry = usePageContextRegistry()

  const askAlpinist = useCallback(
    async ({conversationTitle, prompt, onOpened, pageContext}: AskAlpinistArgs): Promise<void> => {
      setAsking(true)
      // Read before anything opens: the widget is on screen now.
      const page_context = pageContextRegistry?.read(pageContext)
      try {
        const conversation = await createConversation({
          title: conversationTitle,
          category: 'general',
        }).unwrap()
        openAlpinistPanel(conversation.id)
        onOpened?.()
        await createMessage({conversation_id: conversation.id, text: prompt, page_context}).unwrap()
      } catch {
        showNotification({message: t('shortcut_failed'), severity: 'error'})
      } finally {
        setAsking(false)
      }
    },
    [createConversation, createMessage, pageContextRegistry, showNotification, t],
  )

  return {askAlpinist, asking}
}
